import React, { useState, useEffect, useCallback } from "react";
import ApiloNotesButton from './ApiloNotesButton';
import CopyableField from './CopyableField';
import axios from "axios";

const API = "/api";

interface ApiloStatus {
  configured: boolean;
  url: string;
  client_id: string;
  has_secret: boolean;
  token_expires: string;
  refresh_expires: string;
  total_count?: number | null;
}

interface OrderItem {
  name?: string;
  sku?: string;
  ean?: string;
  quantity?: number;
  price?: string;
}

interface ApiloOrderRow {
  apilo_id: string;
  id_external: string;
  customer_name: string;
  customer_login: string;
  phone: string;
  email: string;
  city: string;
  zip_code: string;
  recipient_name?: string | null;
  recipient_city?: string | null;
  recipient_phone?: string | null;
  recipient_street?: string | null;
  invoice_name?: string | null;
  invoice_nip?: string | null;
  status: number | null;
  amount_total: number | null;
  currency: string | null;
  ordered_at: string | null;
  items: OrderItem[];
  details_fetched: boolean;
}

interface Props {
  onBack: () => void;
  currentUser?: any;
  onOpenReturn?: (ref: string) => void;
  onOpenShipment?: (orderId: string, accountId: string) => void;
}

const PAGE_SIZE = 50;

const s = {
  input: { width: "100%", padding: "8px 11px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, fontFamily: "system-ui" },
  btn: { padding: "8px 16px", borderRadius: 6, border: "none", fontSize: 13, cursor: "pointer", fontWeight: 500 as const },
  btnPrimary: { background: "#0369a1", color: "#fff" },
  btnGreen: { background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" },
  btnDanger: { background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" },
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 18, marginBottom: 14 },
  label: { fontSize: 12, color: "#6b7280", marginBottom: 4, display: "block" as const },
  th: { textAlign: "left" as const, padding: "8px 10px", fontSize: 11, fontWeight: 600 as const, color: "#6b7280", borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap" as const },
  td: { padding: "8px 10px", fontSize: 12, color: "#374151", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" as const },
};

function formatDay(iso: string | null): string {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

function money(v: number | null, cur: string | null): string {
  if (v == null) return "\u2014";
  try {
    return v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (cur ? " " + cur : " z\u0142");
  } catch {
    return String(v);
  }
}

export default function ApiloModule({ onBack, currentUser, onOpenReturn, onOpenShipment }: Props) {
  const [status, setStatus] = useState<ApiloStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ url: "", client_id: "", client_secret: "", auth_code: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [showOnb, setShowOnb] = useState(true); // overlay onboardingu Apilo (gdy niepodlaczone)
  const [linkMsg, setLinkMsg] = useState<string | null>(null);  // popup "brak zwrotu/przesylki"
  const [linkBusy, setLinkBusy] = useState<string | null>(null); // apilo_id ktore wlasnie sprawdzamy
  // Sprawdza zwrot/przesylke dla zamowienia Allegro (po id_external) i nawiguje lub pokazuje popup.
  const resolveLink = async (order: ApiloOrderRow, kind: "return" | "shipment") => {
    const oid = order.id_external;
    if (!oid) { setLinkMsg("Brak numeru zamowienia Allegro."); return; }
    setLinkBusy((order.apilo_id || oid) + kind);
    try {
      const r = await axios.get(API + "/apilo/orders/resolve", { params: { order_id: oid, kind } });
      const d = r.data || {};
      if (!d.found) {
        setLinkMsg(kind === "return" ? "Brak zwrotu dla tego zamowienia." : "Brak przesylki dla tego zamowienia.");
      } else if (kind === "return") {
        if (onOpenReturn && d.reference_number) onOpenReturn(d.reference_number);
      } else {
        if (onOpenShipment) onOpenShipment(d.order_id, d.account_id || "");
      }
    } catch (e: any) {
      setLinkMsg("Nie udalo sie sprawdzic. Sprobuj ponownie.");
    }
    setLinkBusy(null);
  };
  const isAllegroOrder = (order: ApiloOrderRow) => !!(order.email && order.email.toLowerCase().endsWith("@allegromail.pl"));

  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<ApiloOrderRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [lastQuery, setLastQuery] = useState("");
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortBy, setSortBy] = useState<string>("");   // kolumna sortowania (puste = domyslnie data desc)
  const [sortDir, setSortDir] = useState<string>("asc");
  // Daty domyslne: od = rok wstecz, do = dzis (format YYYY-MM-DD do input type=date)
  const _fmtDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const _today = new Date();
  const _yearAgo = new Date(); _yearAgo.setFullYear(_yearAgo.getFullYear() - 1);
  const [dateFrom, setDateFrom] = useState<string>(_fmtDate(_yearAgo));
  const [dateTo, setDateTo] = useState<string>(_fmtDate(_today));
  const isMobile = window.innerWidth < 768;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API + "/settings/apilo");
      setStatus(r.data);
      if (r.data?.configured) {
        try {
          const t = await axios.post(API + "/settings/apilo/test");
          setStatus((prev: ApiloStatus | null) => prev ? { ...prev, total_count: t.data?.total_count } : prev);
        } catch { /* test opcjonalny */ }
      }
    } catch (e: any) {
      setMsg({ kind: "err", text: "Nie udalo sie pobrac statusu Apilo." });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runSearch = useCallback(async (query: string, off: number, sb: string = "", sd: string = "asc", df: string = "", dt: string = "") => {
    setSearching(true);
    setSearchErr(null);
    try {
      const _params: any = { q: query, limit: PAGE_SIZE, offset: off };
      if (sb) { _params.sort = sb; _params.dir = sd; }
      if (df) _params.date_from = df;
      if (dt) _params.date_to = dt;
      const r = await axios.get(API + "/apilo/orders/search", { params: _params });
      setResults(r.data?.orders || []);
      setTotal(r.data?.total || 0);
      setOffset(off);
      setLastQuery(query);
    } catch (e: any) {
      setSearchErr(e?.response?.data?.detail || "Nie udalo sie wyszukac. Sprobuj ponownie.");
      setResults([]);
      setTotal(0);
    }
    setSearching(false);
  }, []);

  const doSearch = () => { setExpanded({}); runSearch(q.trim(), 0, sortBy, sortDir, dateFrom, dateTo); };

  const goPage = (newOff: number) => {
    setExpanded({});
    runSearch(lastQuery, newOff, sortBy, sortDir, dateFrom, dateTo);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* ignore */ }
  };
  // Sortowanie po kolumnie (spojne z Offers): ten sam col -> zmien kierunek, inny -> asc. Sortuje CALOSC (backend).
  const handleSort = (col: string) => {
    const nextDir = (sortBy === col && sortDir === "asc") ? "desc" : "asc";
    const nextBy = col;
    setSortBy(nextBy); setSortDir(nextDir);
    setExpanded({});
    runSearch(lastQuery, 0, nextBy, nextDir, dateFrom, dateTo);
  };
  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <span style={{ color: "#d1d5db", marginLeft: 3, fontSize: 10 }}>{"\u2195"}</span>;
    return <span style={{ color: "#1d4ed8", marginLeft: 3, fontSize: 10 }}>{sortDir === "asc" ? "\u2191" : "\u2193"}</span>;
  };

  const connect = async () => {
    if (!form.url || !form.client_id || !form.client_secret || !form.auth_code) {
      setMsg({ kind: "err", text: "Wypelnij wszystkie pola." });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await axios.post(API + "/settings/apilo/connect", form);
      setStatus(r.data);
      setForm({ url: "", client_id: "", client_secret: "", auth_code: "" });
      const cnt = r.data?.total_count;
      setMsg({ kind: "ok", text: cnt != null ? `Polaczono z Apilo \u2014 ${cnt.toLocaleString("pl-PL")} zamowien.` : "Polaczono z Apilo." });
    } catch (e: any) {
      const detail = e?.response?.data?.detail || "Nie udalo sie polaczyc. Sprawdz dane i czy kod autoryzacji nie wygasl.";
      setMsg({ kind: "err", text: detail });
    }
    setBusy(false);
  };

  const testConn = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const t = await axios.post(API + "/settings/apilo/test");
      setStatus((prev: ApiloStatus | null) => prev ? { ...prev, total_count: t.data?.total_count } : prev);
      setMsg({ kind: "ok", text: `Polaczenie dziala \u2014 ${Number(t.data?.total_count).toLocaleString("pl-PL")} zamowien.` });
    } catch (e: any) {
      const detail = e?.response?.data?.detail || "Test polaczenia nie powiodl sie.";
      setMsg({ kind: "err", text: detail });
    }
    setBusy(false);
  };

  const disconnect = async () => {
    if (!window.confirm("Na pewno rozlaczyc Apilo? Trzeba bedzie wpisac klucz ponownie.")) return;
    setBusy(true);
    setMsg(null);
    try {
      await axios.delete(API + "/settings/apilo");
      setStatus({ configured: false, url: "", client_id: "", has_secret: false, token_expires: "", refresh_expires: "" });
      setResults(null);
      setMsg({ kind: "ok", text: "Rozlaczono Apilo." });
    } catch {
      setMsg({ kind: "err", text: "Nie udalo sie rozlaczyc." });
    }
    setBusy(false);
  };

  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + PAGE_SIZE, total);
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  const onbOverlay = (!loading && !status?.configured && showOnb) ? (
    <div style={{ position: "fixed", inset: 0, zIndex: 10001, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 560, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "92vh" }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #eef2f7", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{"Konfiguracja Apilo"}</span>
        </div>
        <div style={{ padding: "28px 32px", overflowY: "auto", flex: 1 }}>
          <div style={{ fontSize: 44, margin: "4px 0 12px", textAlign: "center" }}>{"\uD83D\uDD0D"}</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: "0 0 8px", textAlign: "center" }}>{"Pod\u0142\u0105cz Apilo"}</h1>
          <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.5, textAlign: "center" }}>
            {"Po pod\u0142\u0105czeniu przeszukasz wszystkie zam\u00f3wienia po nazwisku, mie\u015bcie, telefonie, fragmencie numeru, a tak\u017ce SKU i EAN produktu \u2014 czego wbudowana wyszukiwarka Apilo nie potrafi."}
          </p>
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
            {[
              ["1", "Wejd\u017a w panel Apilo", "Administracja \u2192 Klucze API Apilo \u2192 Nowa aplikacja REST API."],
              ["2", "Skopiuj dane aplikacji", "Adres API, Client ID i Client Secret, a potem wygeneruj jednorazowy kod autoryzacji."],
              ["3", "Wklej poni\u017cej i po\u0142\u0105cz", "Dost\u0119p odnowi si\u0119 potem sam \u2014 nie musisz nic robi\u0107 r\u0119cznie."],
            ].map(([n, tytul, opis]) => (
              <div key={n} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: "#15803d", color: "#fff", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{n}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#166534" }}>{tytul}</div>
                  <div style={{ fontSize: 12, color: "#15803d", lineHeight: 1.5 }}>{opis}</div>
                </div>
              </div>
            ))}
          </div>
          {msg && (
            <div style={{ padding: "10px 12px", borderRadius: 9, marginBottom: 12, fontSize: 13,
              background: msg.kind === "ok" ? "#f0fdf4" : "#fef2f2",
              color: msg.kind === "ok" ? "#15803d" : "#dc2626",
              border: "1px solid " + (msg.kind === "ok" ? "#bbf7d0" : "#fecaca") }}>
              {msg.text}
            </div>
          )}
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6, marginTop: 4 }}>{"Adres API (endpoint)"}</label>
          <input style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #d1d5db", fontSize: 14, marginBottom: 10, boxSizing: "border-box" }} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://twojafirma.apilo.com" />
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6, marginTop: 4 }}>{"Client ID"}</label>
          <input style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #d1d5db", fontSize: 14, marginBottom: 10, boxSizing: "border-box" }} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="np. 2" />
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6, marginTop: 4 }}>{"Client Secret"}</label>
          <input style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #d1d5db", fontSize: 14, marginBottom: 10, boxSizing: "border-box" }} value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder="klucz z panelu Apilo" />
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6, marginTop: 4 }}>{"Kod autoryzacji"}</label>
          <input style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #d1d5db", fontSize: 14, marginBottom: 10, boxSizing: "border-box" }} value={form.auth_code} onChange={(e) => setForm({ ...form, auth_code: e.target.value })} placeholder="jednorazowy kod z panelu" />
        </div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid #eef2f7", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <button style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: "transparent", color: "#9ca3af", fontSize: 12, cursor: "pointer" }} onClick={() => setShowOnb(false)}>{"Pomi\u0144"}</button>
          <button style={{ padding: "11px 22px", borderRadius: 10, border: "none", background: "#15803d", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer", flexShrink: 0, opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={connect}>
            {busy ? "\u0141\u0105cz\u0119\u2026" : "Po\u0142\u0105cz z Apilo \u2192"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "system-ui, sans-serif", color: "#111827" }}>
      {onbOverlay}
      {linkMsg && (
        <div onClick={() => setLinkMsg(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 12, padding: "24px 28px", maxWidth: 380, boxShadow: "0 10px 40px rgba(0,0,0,0.2)", textAlign: "center" }}>
            <div style={{ fontSize: 15, color: "#374151", marginBottom: 18 }}>{linkMsg}</div>
            <button onClick={() => setLinkMsg(null)}
              style={{ padding: "8px 24px", borderRadius: 8, border: "none", background: "#0369a1", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              OK
            </button>
          </div>
        </div>
      )}
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>&#8592;</button>
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Apilo</h1>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Wyszukiwarka zamowien</div>
          </div>
        </div>

        <div style={{ maxWidth: status?.configured ? 1400 : 720 }}>
        {msg && (
          <div style={{ padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 13,
            background: msg.kind === "ok" ? "#f0fdf4" : "#fef2f2",
            color: msg.kind === "ok" ? "#15803d" : "#dc2626",
            border: "1px solid " + (msg.kind === "ok" ? "#bbf7d0" : "#fecaca") }}>
            {msg.text}
          </div>
        )}

        {loading ? (
          <div style={{ ...s.card, color: "#6b7280", fontSize: 13 }}>Ladowanie...</div>
        ) : status?.configured ? (
          <div>
            <div style={{ ...s.card, borderColor: "#bbf7d0", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>{"\u2705"}</span>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#15803d" }}>
                  Polaczono z Apilo
                  {status.total_count != null && <span style={{ fontWeight: 400, color: "#166534" }}> {"\u2014"} {status.total_count.toLocaleString("pl-PL")} zamowien</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={testConn} disabled={busy} style={{ ...s.btn, ...s.btnGreen, opacity: busy ? 0.6 : 1 }}>
                  {busy ? "..." : "Testuj polaczenie"}
                </button>
                <button onClick={disconnect} disabled={busy} style={{ ...s.btn, ...s.btnDanger, opacity: busy ? 0.6 : 1 }}>
                  Rozlacz
                </button>
              </div>
            </div>

            <div style={{ ...s.card }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
                  placeholder={isMobile ? "Szukaj zamowien..." : "Szukaj zamowien: nazwisko, miasto, telefon, numer, login, e-mail, SKU, EAN..."}
                  autoFocus
                  style={{ ...s.input, height: 46, fontSize: 15, padding: "0 14px", flex: isMobile ? "1 1 100%" : "1 1 auto", minWidth: 0 }}
                />
                <div style={{ display: "flex", gap: 8, alignItems: "center", flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flex: isMobile ? "1 1 0" : "0 0 auto" }}>
                    <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>{"od"}</span>
                    <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)}
                      style={{ ...s.input, height: 46, fontSize: 13, padding: "0 8px", width: isMobile ? "100%" : 140 }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flex: isMobile ? "1 1 0" : "0 0 auto" }}>
                    <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>{"do"}</span>
                    <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)}
                      style={{ ...s.input, height: 46, fontSize: 13, padding: "0 8px", width: isMobile ? "100%" : 140 }} />
                  </div>
                  <button onClick={doSearch} disabled={searching}
                    style={{ ...s.btn, ...s.btnPrimary, height: 46, padding: "0 22px", fontSize: 14, opacity: searching ? 0.6 : 1, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {searching ? "Szukam..." : "\uD83D\uDD0D Szukaj"}
                  </button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8, lineHeight: 1.6 }}>
                <div style={{ marginBottom: 4 }}>
                  Mozesz {"\u0142\u0105czy\u0107"} slowa {"\u2014"} kazde zawezi wynik. Np. <strong>Kowalski Warszawa</strong> pokaze tylko Kowalskich z Warszawy.
                </div>
                <div>
                  {"Chcesz szuka\u0107 tylko w jednym polu? Dodaj przedrostek, np. "}
                  <strong>{"sku: 1331"}</strong>{", "}<strong>{"ean: 590..."}</strong>{", "}<strong>{"zam\u00f3wienie: 1152"}</strong>{", "}
                  <strong>{"login: kowal"}</strong>{" (lub "}<strong>{"u\u017cytkownik:"}</strong>{"), "}<strong>{"telefon: 512"}</strong>{", "}
                  <strong>{"email: jan@"}</strong>{", "}<strong>{"miasto: Krak\u00f3w"}</strong>{", "}<strong>{"nazwisko: Nowak"}</strong>{"."}
                </div>
              </div>
            </div>

            {searchErr && (
              <div style={{ ...s.card, background: "#fef2f2", borderColor: "#fecaca", color: "#dc2626", fontSize: 13 }}>
                {searchErr}
              </div>
            )}

            {results !== null && !searchErr && (
              <div style={{ ...s.card, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", fontSize: 13, color: "#374151" }}>
                  {total > 0
                    ? <>Znaleziono <strong>{total.toLocaleString("pl-PL")}</strong> {total === 1 ? "zamowienie" : "zamowien"}{lastQuery ? <> dla <strong>{"\u201E"}{lastQuery}{"\u201D"}</strong></> : ""} {"\u2014"} pokazuje {pageFrom}{"\u2013"}{pageTo}</>
                    : <>Brak wynikow{lastQuery ? <> dla <strong>{"\u201E"}{lastQuery}{"\u201D"}</strong></> : ""}. Sprobuj innej frazy.</>}
                </div>

                {total > 0 && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ ...s.th, cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("order_no")}>Zamowienie <SortIcon col="order_no" /></th>
                          <th style={{ ...s.th, cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("name")}>Klient <SortIcon col="name" /></th>
                          <th style={{ ...s.th, cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("login")}>Login <SortIcon col="login" /></th>
                          <th style={{ ...s.th, cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("phone")}>Telefon <SortIcon col="phone" /></th>
                          <th style={{ ...s.th, cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("city")}>Miasto <SortIcon col="city" /></th>
                          <th style={{ ...s.th, textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("amount")}>Kwota <SortIcon col="amount" /></th>
                          <th style={{ ...s.th, cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("date")}>Data <SortIcon col="date" /></th>
                          <th style={s.th}>Produkty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((o) => {
                          const key = o.apilo_id || o.id_external;
                          const isOpen = !!expanded[key];
                          const items = o.items || [];
                          const shown = isOpen ? items : items.slice(0, 2);
                          return (
                            <tr key={key}>
                              <td style={{ ...s.td, fontFamily: "monospace", whiteSpace: "nowrap", verticalAlign: "top" }}>
                                <div>{o.apilo_id ? <CopyableField label="nr" value={o.apilo_id} hideLabel /> : "\u2014"}</div>
                                {isAllegroOrder(o) && (
                                  <div style={{ display: "flex", gap: 6, marginTop: 6, fontFamily: "system-ui" }}>
                                    <button onClick={() => resolveLink(o, "return")} disabled={linkBusy === (o.apilo_id || o.id_external) + "return"}
                                      style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #fdba74", background: "#fff7ed", color: "#c2410c", cursor: "pointer", whiteSpace: "nowrap" }}>
                                      {linkBusy === (o.apilo_id || o.id_external) + "return" ? "..." : "\u21A9\uFE0F Zwrot"}
                                    </button>
                                    <button onClick={() => resolveLink(o, "shipment")} disabled={linkBusy === (o.apilo_id || o.id_external) + "shipment"}
                                      style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #93c5fd", background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", whiteSpace: "nowrap" }}>
                                      {linkBusy === (o.apilo_id || o.id_external) + "shipment" ? "..." : "\uD83D\uDCE6 Przesylka"}
                                    </button>
                                  </div>
                                )}
                                {o.id_external && (
                                  <div style={{ display: "flex", gap: 6, marginTop: 6, fontFamily: "system-ui" }}>
                                    <ApiloNotesButton orderId={o.id_external} variant="inline" />
                                  </div>
                                )}
                              </td>
                              <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                                <div style={{ fontWeight: 600, color: "#111827" }}>{o.customer_name ? <CopyableField label="klient" value={o.customer_name} hideLabel /> : "\u2014"}</div>
                                {o.email && <div style={{ fontSize: 11, color: "#6b7280" }}>{o.email}</div>}
                                {o.recipient_name && o.recipient_name.trim().toLowerCase() !== (o.customer_name || "").trim().toLowerCase() && (
                                  <div style={{ fontSize: 11, color: "#ea580c", fontWeight: 600, marginTop: 2 }}>{"\u2192 do: "}<CopyableField label="odbiorca" value={o.recipient_name} hideLabel /></div>
                                )}
                              </td>
                              <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                                {o.customer_login
                                  ? <CopyableField label="login" value={o.customer_login} hideLabel />
                                  : <span style={{ color: "#9ca3af", fontStyle: "italic" }}>{o.details_fetched ? "\u2014" : "wczytuje..."}</span>}
                              </td>
                              <td style={{ ...s.td, whiteSpace: "nowrap" }}>{o.phone ? <CopyableField label="telefon" value={o.phone} hideLabel /> : "\u2014"}</td>
                              <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                                {o.city ? <CopyableField label="miasto" value={o.city} hideLabel /> : "\u2014"}
                                {o.zip_code && <div style={{ fontSize: 11, color: "#9ca3af" }}>{o.zip_code}</div>}
                              </td>
                              <td style={{ ...s.td, textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>{money(o.amount_total, o.currency)}</td>
                              <td style={{ ...s.td, whiteSpace: "nowrap" }}>{formatDay(o.ordered_at)}</td>
                              <td style={{ ...s.td, minWidth: 220 }}>
                                {items.length === 0 ? <span style={{ color: "#9ca3af" }}>{"\u2014"}</span> : (
                                  <div>
                                    {shown.map((it, i) => (
                                      <div key={i} style={{ marginBottom: 3 }}>
                                        <span style={{ color: "#374151" }}>{it.quantity ? `${it.quantity}\u00d7 ` : ""}{it.name || it.sku || it.ean || "produkt"}</span>
                                        {it.sku && <span style={{ fontSize: 10, color: "#9ca3af" }}> {"\u00b7"} SKU <CopyableField label="SKU" value={it.sku} hideLabel /></span>}
                                      </div>
                                    ))}
                                    {items.length > 2 && (
                                      <button
                                        onClick={() => setExpanded((p) => ({ ...p, [key]: !isOpen }))}
                                        style={{ background: "none", border: "none", color: "#0369a1", cursor: "pointer", fontSize: 11, padding: 0, marginTop: 2 }}>
                                        {isOpen ? "\u25B2 zwin" : `\u25BC pokaz wszystkie (${items.length})`}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {total > PAGE_SIZE && (
                  <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
                    <button onClick={() => goPage(offset - PAGE_SIZE)} disabled={!hasPrev || searching}
                      style={{ ...s.btn, background: hasPrev ? "#f1f5f9" : "#f8fafc", color: hasPrev ? "#374151" : "#cbd5e1", cursor: hasPrev ? "pointer" : "default" }}>
                      {"\u2190"} Poprzednie
                    </button>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      Strona {Math.floor(offset / PAGE_SIZE) + 1} z {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                    </span>
                    <button onClick={() => goPage(offset + PAGE_SIZE)} disabled={!hasNext || searching}
                      style={{ ...s.btn, background: hasNext ? "#f1f5f9" : "#f8fafc", color: hasNext ? "#374151" : "#cbd5e1", cursor: hasNext ? "pointer" : "default" }}>
                      Nastepne {"\u2192"}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ ...s.card, background: "#f8fafc", borderColor: "#e2e8f0", fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
              <strong style={{ color: "#475569" }}>{"\u2139\uFE0F"} O synchronizacji:</strong> zamowienia sa kopiowane z Apilo do lokalnej bazy, dzieki czemu
              wyszukiwanie jest natychmiastowe i dziala po nazwisku, miescie czy loginie {"\u2014"} czego wbudowana wyszukiwarka Apilo nie potrafi.
              Loginy klientow dociagaja sie w tle (przy duzej liczbie zamowien moze to potrwac kilka{"\u2013"}kilkanascie godzin od pierwszego polaczenia).
              Po zakonczeniu system automatycznie dobiera nowe zamowienia na biezaco {"\u2014"} nie trzeba nic robic recznie.
            </div>
          </div>
        ) : (
          <div>
            <div style={{ ...s.card }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Podlacz konto Apilo</h2>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 18, lineHeight: 1.6 }}>
                Wpisz dane z panelu Apilo. Znajdziesz je w:&nbsp;
                <strong>Administracja {"\u2192"} Klucze API Apilo {"\u2192"} Nowa aplikacja REST API</strong>.
                Kod autoryzacji jest jednorazowy {"\u2014"} po polaczeniu dostep odnawia sie automatycznie.
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={s.label}>Adres API (endpoint)</label>
                <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://twojafirma.apilo.com" style={s.input} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={s.label}>Client ID</label>
                <input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  placeholder="np. 2" style={s.input} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={s.label}>Client Secret</label>
                <input value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                  placeholder="klucz z panelu Apilo" style={s.input} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>Kod autoryzacji</label>
                <input value={form.auth_code} onChange={(e) => setForm({ ...form, auth_code: e.target.value })}
                  placeholder="jednorazowy kod z panelu" style={s.input} />
              </div>

              <button onClick={connect} disabled={busy} style={{ ...s.btn, ...s.btnPrimary, opacity: busy ? 0.6 : 1 }}>
                {busy ? "Laczenie..." : "Polacz z Apilo"}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
